import {
  collectPlaybookSections,
  getPlaybookNode,
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { useCallback, useMemo, useState } from "react";
import type { Key } from "react-aria";
import { z } from "zod";

/** Describes the objects used to identify items in the assessment tree UI. */
const assessmentTreeItemKeySchema = z.union([
  z.object({ type: z.literal("section"), id: z.string() }),
  z.object({ type: z.literal("criteria"), id: z.string() }),
  z.object({ type: z.literal("outcome"), id: z.string() }),
  z.object({ type: z.literal("knowledge-base"), id: z.string() }),
  z.object({ type: z.literal("summary-template"), id: z.string() }),
  z.object({ type: z.literal("new-section") }),
  z.object({ type: z.literal("new-subsection"), sectionId: z.string() }),
  z.object({ type: z.literal("new-criteria"), sectionId: z.string() }),
  // A new-criterion proposal saved by the AI assistant. The id is the
  // `playbook_node_id` from `useSampleToolResults` — a uuid minted by the
  // tool that intentionally does NOT exist in the playbook tree.
  z.object({ type: z.literal("ghost-criterion"), id: z.string() }),
  // A new-section proposal saved by the AI assistant. Like ghost-criterion,
  // its id is a uuid minted by the section sample tool that intentionally
  // does NOT exist in the playbook tree.
  z.object({ type: z.literal("ghost-section"), id: z.string() }),
]);

/** Identifies items in the assessment tree UI. */
export type AssessmentTreeItemKey = z.infer<typeof assessmentTreeItemKeySchema>;

const defaultAssessmentTreeItemKey = {
  type: "outcome",
  id: "outcome",
} as const satisfies AssessmentTreeItemKey;

/**
 * Normalize an external initial selection to a tree item this editor renders.
 * Transient action/ghost keys are never valid initial selections.
 */
export function resolveInitialAssessmentTreeItemKey(
  template: PlaybookTree,
  initialSelectedKey: AssessmentTreeItemKey | undefined,
): AssessmentTreeItemKey {
  if (!initialSelectedKey) {
    return defaultAssessmentTreeItemKey;
  }

  switch (initialSelectedKey.type) {
    case "outcome":
      return initialSelectedKey.id === "outcome"
        ? initialSelectedKey
        : defaultAssessmentTreeItemKey;
    case "summary-template":
      return initialSelectedKey.id === "summary-template"
        ? initialSelectedKey
        : defaultAssessmentTreeItemKey;
    case "section": {
      const node = getPlaybookNode(initialSelectedKey.id, template);
      return node && isPlaybookSectionNode(node)
        ? initialSelectedKey
        : defaultAssessmentTreeItemKey;
    }
    case "criteria": {
      const node = getPlaybookNode(initialSelectedKey.id, template);
      return node && isPlaybookCriterionNode(node)
        ? initialSelectedKey
        : defaultAssessmentTreeItemKey;
    }
    default:
      return defaultAssessmentTreeItemKey;
  }
}

/** Converts a tree item key to a string for use with React Aria. */
export function serializeAssessmentTreeItemKey(
  key: AssessmentTreeItemKey,
): Key & string {
  // Parse to strip any extraneous properties
  return JSON.stringify(assessmentTreeItemKeySchema.parse(key));
}

/** Converts a React Aria key back to a tree item key. */
export function deserializeAssessmentTreeItemKey(
  key: Key,
): AssessmentTreeItemKey | undefined {
  if (typeof key !== "string") {
    return undefined;
  }

  const result = assessmentTreeItemKeySchema.safeParse(JSON.parse(key));
  return result.success ? result.data : undefined;
}

/** Hook to manage expansion/collapse state of assessment tree items */
export function useAssessmentTreeExpandAndCollapse(template: PlaybookTree) {
  // Track collapsed section IDs (simpler approach like courseTreeHelpers)
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<
    readonly string[]
  >([]);

  // Memoized function to get all section IDs from the template
  const allSectionIds = useMemo(() => {
    if (!template) {
      return [];
    }

    return collectPlaybookSections(template).map((section) => section.id);
  }, [template]);

  // Derive expanded keys from collapsed state (like courseTreeHelpers)
  const expandedKeys = useMemo(
    () =>
      allSectionIds
        .filter((sectionId) => !collapsedSectionIds.includes(sectionId))
        .map((sectionId) =>
          serializeAssessmentTreeItemKey({ type: "section", id: sectionId }),
        ),
    [allSectionIds, collapsedSectionIds],
  );

  const onExpandedChange = useCallback(
    (keys: Set<Key>) => {
      const expandedSectionIds = Array.from(keys)
        .map(deserializeAssessmentTreeItemKey)
        .map((key) => (key?.type === "section" ? key.id : undefined))
        .filter(isNotUndefined);

      const newCollapsedIds = allSectionIds.filter(
        (id) => !expandedSectionIds.includes(id),
      );

      setCollapsedSectionIds(newCollapsedIds);
    },
    [allSectionIds],
  );

  return {
    expandedKeys,
    onExpandedChange,
  };
}
